import { useEffect, useRef, useState } from "react";
import NewWindow from "react-new-window";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import Download from "@mui/icons-material/Download";
import { Button, IconButton } from "@mui/material";
import QAForm from "./QAForm";
import useWindowDimensions from "../Hooks/useWindowDimensions";
import { getRecordingFiles } from "../Store/Actions/CallAction";

const ActionColumnQA = ({
  params,
  setIsQAFormDialogShown,
  setSelectedLogItem,
}) => {
  const [isNewWindowOpen, setIsNewWindowOpen] = useState(false);
  const [isRecordingLoading, setIsRecordingLoading] = useState(false);
  const [isRecordingPlaying, setIsRecordingPlaying] = useState(false);
  const [isSeekVoiceAllowed, setIsSeekVoiceAllowed] = useState(null);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const { agentPermission } = useSelector((state) => state.auth);
  const recordingRef = useRef(null);
  const { width } = useWindowDimensions();

  // Get seek voice permission
  useEffect(() => {
    const seekVoicePermission = agentPermission.find(
      (permission) => permission.PermissionCode === "User.SeekVoice"
    );

    if (seekVoicePermission && seekVoicePermission.PermissionAccess === "A") {
      setIsSeekVoiceAllowed(true);
    } else {
      setIsSeekVoiceAllowed(false);
    }
  }, [agentPermission]);

  // Event listener for audio `ended` event
  useEffect(() => {
    recordingRef.current &&
      recordingRef.current.addEventListener("ended", () =>
        setIsRecordingPlaying(false)
      );

    return () => {
      recordingRef.current &&
        recordingRef.current.removeEventListener("ended", () =>
          setIsRecordingPlaying(false)
        );
    };
  }, [recordingRef.current]);

  return (
    <>
      <IconButton
        onClick={() => {
          if (params.row.acdname === "VideoCall Device") {
            setSelectedLogItem(params.row);
            setIsQAFormDialogShown(true);
          } else {
            if (!selectedRecording) {
              setIsRecordingLoading(true);
              setIsNewWindowOpen(true);

              getRecordingFiles(params.row.recmessageid)
                .then((response) => {
                  if (response.status) {
                    setSelectedRecording(response.data);

                    recordingRef.current = new Audio(
                      `data:audio/wav;base64,${response.data.filebytes}`
                    );

                    setIsRecordingLoading(false);
                    setSelectedLogItem(params.row);
                  } else {
                    console.log("error", response.error);
                    toast.error(response.error);
                    setIsRecordingLoading(false);
                    setIsNewWindowOpen(false);
                  }
                })
                .catch((err) => {
                  console.log("err", err);
                  toast.error(err);
                  setIsRecordingLoading(false);
                  setIsNewWindowOpen(false);
                });
            } else if (isRecordingLoading) {
              return;
            }
          }
        }}
      >
        <Icon
          color="#7e7e7e"
          height="26"
          icon={
            isRecordingLoading
              ? "eos-icons:loading"
              : "ic:baseline-remove-red-eye"
          }
          width="26"
        />
      </IconButton>

      {params.row.acdname === "VideoCall Device" && (
        <>
          <IconButton
            onClick={() => {
              if (!selectedRecording) {
                setIsRecordingLoading(true);

                getRecordingFiles(params.row.recmessageid)
                  .then((response) => {
                    if (response.status) {
                      setSelectedRecording(response.data);

                      recordingRef.current = new Audio(
                        `data:audio/wav;base64,${response.data.filebytes}`
                      );

                      setIsRecordingLoading(false);
                    } else {
                      console.log("error", response.error);
                      toast.error(response.error);
                      setIsRecordingLoading(false);
                    }
                  })
                  .catch((err) => {
                    console.log("err", err);
                    toast.error(err);
                    setIsRecordingLoading(false);
                  });
              } else if (isRecordingLoading) {
                return;
              }
            }}
          >
            <Icon
              color="#7e7e7e"
              height="26"
              icon={isRecordingLoading ? "eos-icons:loading" : "ion:play"}
              width="26"
            />
          </IconButton>

          {selectedRecording && (
            <NewWindow
              features={{
                height: 200,
                width: 400,
              }}
              onUnload={() => setSelectedRecording(null)}
            >
              <div
                className={`controls new-window ${
                  !isSeekVoiceAllowed ? "simple" : ""
                }`}
              >
                {isSeekVoiceAllowed ? (
                  // Audio with controls if seek voice is allowed
                  <audio
                    controls
                    controlsList="nodownload"
                    src={`data:audio/wav;base64,${selectedRecording?.filebytes}`}
                  />
                ) : (
                  // Play/pause button if seek voice is not allowed
                  <Button
                    className="download-button"
                    variant="contained"
                    onClick={() => {
                      if (isRecordingPlaying) {
                        recordingRef.current.pause();
                        setIsRecordingPlaying(false);
                      } else {
                        recordingRef.current.play();
                        setIsRecordingPlaying(true);
                      }
                    }}
                  >
                    {isRecordingPlaying ? (
                      <Icon height="24" icon="ic:round-pause" />
                    ) : (
                      <Icon height="24" icon="ph:play-fill" />
                    )}
                  </Button>
                )}

                <a
                  download={selectedRecording?.filename}
                  href={`data:audio/wav;base64,${selectedRecording?.filebytes}`}
                >
                  <Button variant="contained" className="download-button">
                    <Download />
                  </Button>
                </a>
              </div>
            </NewWindow>
          )}
        </>
      )}

      {isNewWindowOpen && (
        <NewWindow
          copyStyles
          features={{
            height: 550,
            width: width >= 1440 ? 1440 : 900,
          }}
          onUnload={() => {
            setIsQAFormDialogShown(false);
            setSelectedRecording(null);
            setIsNewWindowOpen(false);
          }}
        >
          <QAForm
            caseID={params.row.logacdcallid}
            channelID="1"
            filledComment={params.row.comment}
            filledTotalScore={params.row.totalscore}
            isRecordingLoading={isRecordingLoading}
            isRecordingPlaying={isRecordingPlaying}
            recordingRef={recordingRef}
            scoreItemID={params.row.scoreitemid}
            selectedRecording={selectedRecording}
            setIsNewWindowOpen={setIsNewWindowOpen}
            setIsRecordingPlaying={setIsRecordingPlaying}
          />
        </NewWindow>
      )}
    </>
  );
};

export default ActionColumnQA;
